import { useEffect, useRef, useState } from 'react';
import { FiX, FiDownload, FiCopy } from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';

export function ShareMarketModal({ open, onClose, market }) {
  const cardRef = useRef(null);
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!open || !market) return null;

  // 构建分享链接（使用当前域名或配置的域名）
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const shareUrl = `${baseUrl}/market/${market.id}`;

  // 计算聪明钱统计
  const yesCount = market.holders.yes.length;
  const noCount = market.holders.no.length;

  // 计算聪明钱总金额（简化处理，从 stake 中提取数值）
  const parseStake = (stake) => {
    if (!stake) return 0;
    if (typeof stake === 'number') return stake;
    if (typeof stake !== 'string') return 0;

    const lowerStake = stake.toLowerCase().replace(/[$,]/g, '').trim();

    let multiplier = 1;
    if (lowerStake.endsWith('k')) multiplier = 1000;
    else if (lowerStake.endsWith('m')) multiplier = 1000000;
    else if (lowerStake.endsWith('b')) multiplier = 1000000000;

    const numericPart = parseFloat(lowerStake);

    if (isNaN(numericPart)) return 0;

    return numericPart * multiplier;
  };

  const yesValue = market.holders.yes.reduce((sum, holder) => sum + parseStake(holder.stake), 0);
  const noValue = market.holders.no.reduce((sum, holder) => sum + parseStake(holder.stake), 0);

  const formatValue = (value) => {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
    return `$${Math.round(value)}`;
  };

  // 复制图片到剪贴板
  const handleCopyImage = async () => {
    if (!cardRef.current || copying) return;

    try {
      setCopying(true);
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true
      });

      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          alert('图片已复制到剪贴板！');
        } catch (err) {
          console.error('复制失败:', err);
          alert('复制失败，请尝试下载图片');
        } finally {
          setCopying(false);
        }
      });
    } catch (err) {
      console.error('生成图片失败:', err);
      alert('生成图片失败');
      setCopying(false);
    }
  };

  // 下载图片
  const handleDownload = async () => {
    if (!cardRef.current || downloading) return;

    try {
      setDownloading(true);
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true
      });

      const link = document.createElement('a');
      link.download = `polyone-${market.id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('下载失败:', err);
      alert('下载失败');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-950/75 backdrop-blur-[18px] flex items-center justify-center p-4 z-[100]"
      role="dialog"
      aria-modal="true"
    >
      <div className="glass-xl w-full max-w-[560px] max-h-[78vh] overflow-y-auto p-5">
        <div className="flex items-start justify-end mb-4">
          <button
            className="ghost-pill w-9 h-9 inline-flex items-center justify-center bg-slate-900/60 border border-slate-600/60 text-text rounded-3xl cursor-pointer transition-all duration-200 hover:border-accent hover:text-sky-100"
            onClick={onClose}
            aria-label="关闭弹窗"
          >
            <FiX />
          </button>
        </div>

        <div
          ref={cardRef}
          className="market-card-bg glass-lg rounded-[24px] p-5 mb-4 relative overflow-hidden"
          style={market.image ? { '--bg-image': `url(${market.image})` } : {}}
        >
          <div className="flex items-center gap-0.5 font-sans mb-4">
            <span className="text-base md:text-lg font-semibold text-white">Poly</span>
            <span className="text-base md:text-lg font-semibold px-1 rounded-md bg-[#ff9000] text-black ml-0.5">One</span>
          </div>

          <div className="mb-4">
            <p className="uppercase tracking-[0.3em] text-[0.5rem] text-text-muted mb-1.5">
              {market.category.split('·')[0].trim()}
            </p>
            <h3 className="text-lg md:text-xl font-bold text-white leading-tight mb-2">
              {market.title}
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-2.5 mb-4">
            <div className="stat-card-volume p-2.5 rounded-2xl">
              <p className="text-text-muted text-[0.55rem] font-medium mb-0.5">24小时交易额</p>
              <strong className="text-xs md:text-sm text-text">{market.metrics.volume}</strong>
            </div>
            <div className="stat-card-deadline p-2.5 rounded-2xl">
              <p className="text-text-muted text-[0.55rem] font-medium mb-0.5">截止时间</p>
              <strong className="text-xs md:text-sm text-text">{market.deadline}</strong>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-white font-semibold text-xs md:text-sm mb-1.5">市场概率</p>
            <div className="relative flex items-stretch rounded-full h-9 shadow-[inset_0_1px_2px_rgba(255,255,255,0.1),0_4px_12px_rgba(0,0,0,0.3)]">
              <div
                className="diverging-meter__segment yes flex items-center justify-center font-semibold text-[0.7rem] border-y border-l border-emerald-600/40 rounded-l-full backdrop-blur-sm"
                style={{ width: `${market.probYes}%` }}
              >
                {market.probYes}%
              </div>
              <div
                className="diverging-meter__segment no flex items-center justify-center font-semibold text-[0.7rem] border-y border-r border-red-600/40 rounded-r-full backdrop-blur-sm"
                style={{ width: `${market.probNo}%` }}
              >
                {market.probNo}%
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5 mb-4">
            <div className="p-2.5 rounded-2xl bg-slate-900/70 border border-slate-600/55">
              <p className="text-text-muted text-[0.55rem] font-medium mb-0.5">聪明钱地址</p>
              <div className="flex items-baseline gap-2">
                <strong className="text-emerald-400 text-xs md:text-sm">{yesCount}</strong>
                <span className="text-text-muted text-[0.5rem]">vs</span>
                <strong className="text-red-400 text-xs md:text-sm">{noCount}</strong>
              </div>
            </div>
            <div className="p-2.5 rounded-2xl bg-slate-900/70 border border-slate-600/55">
              <p className="text-text-muted text-[0.55rem] font-medium mb-0.5">聪明钱金额</p>
              <div className="flex items-baseline gap-2">
                <strong className="text-emerald-400 text-xs md:text-sm">{formatValue(yesValue)}</strong>
                <span className="text-text-muted text-[0.5rem]">vs</span>
                <strong className="text-red-400 text-xs md:text-sm">{formatValue(noValue)}</strong>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-muted text-[0.55rem] mb-0.5">扫码查看详情</p>
              <p className="text-text text-[0.5rem] leading-5 break-all">{shareUrl}</p>
            </div>
            <div className="bg-white p-2 rounded-xl">
              <QRCodeCanvas
                value={shareUrl}
                size={72}
                level="M"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2.5">
          <button
            onClick={handleCopyImage}
            disabled={copying}
            className="flex-1 primary-pill h-10 px-4 border-none cursor-pointer font-semibold transition-all duration-200 rounded-3xl inline-flex items-center justify-center text-sm gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiCopy />
            {copying ? '生成中...' : '复制图片'}
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 ghost-pill h-10 px-4 border border-slate-600/60 cursor-pointer font-medium transition-all duration-200 rounded-3xl inline-flex items-center justify-center text-sm gap-2 hover:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload />
            {downloading ? '生成中...' : '下载图片'}
          </button>
        </div>

        <p className="text-text-muted text-[0.7rem] text-center mt-3">
          提示：可直接复制图片后粘贴到 Telegram、微信等平台
        </p>
      </div>
    </div>
  );
}
